import React, { useState } from 'react'
import GradientButton from '../../components/GradientButton'
import LotStoryGroupDeliveryCard from './LotStoryGroupDeliveryCard'
import LotStoryGroupReadPreflightCard from './LotStoryGroupReadPreflightCard'
import LotStoryGroupShareReviewPreflightCard from './LotStoryGroupShareReviewPreflightCard'
import LotStoryGroupShareShellCard from './LotStoryGroupShareShellCard'
import LotStoryPrivateWritePreflightCard from './LotStoryPrivateWritePreflightCard'
import LotStoryLibraryVersionHistory from './LotStoryLibraryVersionHistory'
import type {
  LotStoryLibraryDetailActions,
  LotStoryLibraryDetailState,
  TranslateFn
} from './lotStoryLibraryTypes'
import {
  getStoryFamilyLabel,
  getStoryFamilyTechnicalId,
  type LotStoryLibraryPresentation
} from './lotStoryPresentation'
import {
  getLotStoryArtifactStatusBadgeStyle,
  getLotStoryArtifactStatusLabel,
  getLotStoryArtifactVersionLabel,
  getLotStoryArtifactVisibilityLabel,
  getLotStoryReadinessLabel
} from './lotStoryLibraryUi'
import { resolveStoredActorContextLabel } from './storyActorLabels'

type LotStoryLibraryDetailPanelProps = {
  t: TranslateFn
  detailState: LotStoryLibraryDetailState
  detailActions: LotStoryLibraryDetailActions
  presentation: LotStoryLibraryPresentation
}

type DisclosureSectionProps = {
  title: string
  subtitle: string
  defaultOpen?: boolean
  children: React.ReactNode
}

const disclosureSectionStyle: React.CSSProperties = {
  padding: 12,
  borderRadius: 12,
  border: '1px solid #e2e8f0',
  backgroundColor: '#f8fafc'
}

function DisclosureSection({
  title,
  subtitle,
  defaultOpen = false,
  children
}: Readonly<DisclosureSectionProps>) {
  // Track open state internally so that parent re-renders (which can flip the
  // `defaultOpen` source signal mid-flow) do not auto-close a section the user is
  // actively working in. The native <details> toggle still drives user interaction.
  const [open, setOpen] = useState(defaultOpen)
  return (
    <details
      open={open}
      onToggle={(event) => {
        setOpen((event.currentTarget as HTMLDetailsElement).open)
      }}
      style={disclosureSectionStyle}
    >
      <summary style={{ cursor: 'pointer', listStylePosition: 'outside' }}>
        <div style={{ display: 'inline-grid', gap: 4 }}>
          <strong style={{ fontSize: 13, color: '#0f172a' }}>{title}</strong>
          <div style={{ fontSize: 12, color: '#475569' }}>{subtitle}</div>
        </div>
      </summary>

      <div style={{ marginTop: 12, display: 'grid', gap: 10 }}>
        {children}
      </div>
    </details>
  )
}

export default function LotStoryLibraryDetailPanel({
  t,
  detailState,
  detailActions,
  presentation
}: Readonly<LotStoryLibraryDetailPanelProps>) {
  const {
    selectedStoryArtifact,
    selectedStoryPreviewVersionId,
    canDraftLotStories,
    canRevokeLotStories,
    storyPrivateWritePreflight,
    storyGroupReadPreflight,
    storyGroupDelivery,
    storyGroupDeliveryRenderedReceipt,
    storyGroupShareReviewPreflight,
    storyGroupShareShell,
    storyPublishing,
    storyRevoking
  } = detailState
  const {
    onOpenArtifact,
    onOpenArtifactVersion,
    onSelectGroupReadPreflightGroup,
    onRecordGroupShareReview,
    onRecordGroupShareApproval,
    onRecordGroupShareReleaseGate,
    onPromoteStoryToGroup,
    onSaveGroupShareIntent,
    onPublishArtifact,
    onRevokeArtifact
  } = detailActions
  const {
    lotStoryCardStyle,
    formatHistoryTimestamp,
    renderStoryProjectionReadOnlySections
  } = presentation
  const selectedStoryFamilyLabel = getStoryFamilyLabel(selectedStoryArtifact.version.projectionSnapshot)
  const selectedStoryFamilyTechnicalId = getStoryFamilyTechnicalId(selectedStoryArtifact.version.projectionSnapshot)
  const attributionCards = [
    {
      key: 'draft',
      titleKey: 'lots.story.library.detail.draftedBy' as const,
      identityLabel: resolveStoredActorContextLabel(selectedStoryArtifact.version.createdByContext, undefined, ''),
      actorId: selectedStoryArtifact.version.createdByContext?.actorId ?? selectedStoryArtifact.version.createdByActorId,
      tenantMemberId: selectedStoryArtifact.version.createdByContext?.tenantMemberId,
      userId: selectedStoryArtifact.version.createdByContext?.userId,
      tenantMembershipId: selectedStoryArtifact.version.createdByContext?.tenantMembershipId,
      tokenId: selectedStoryArtifact.version.createdByContext?.tokenId ?? selectedStoryArtifact.version.createdByTokenId,
      tenantKey: selectedStoryArtifact.version.createdByContext?.tenantKey ?? selectedStoryArtifact.version.tenantKey,
      recordedAt: selectedStoryArtifact.version.createdAt
    },
    {
      key: 'publish',
      titleKey: 'lots.story.library.detail.publishedBy' as const,
      identityLabel: resolveStoredActorContextLabel(selectedStoryArtifact.version.publishedByContext, undefined, ''),
      actorId: selectedStoryArtifact.version.publishedByContext?.actorId ?? selectedStoryArtifact.version.publishedByActorId,
      tenantMemberId: selectedStoryArtifact.version.publishedByContext?.tenantMemberId,
      userId: selectedStoryArtifact.version.publishedByContext?.userId,
      tenantMembershipId: selectedStoryArtifact.version.publishedByContext?.tenantMembershipId,
      tokenId: selectedStoryArtifact.version.publishedByContext?.tokenId ?? selectedStoryArtifact.version.publishedByTokenId,
      tenantKey: selectedStoryArtifact.version.publishedByContext?.tenantKey ?? selectedStoryArtifact.version.tenantKey,
      recordedAt: selectedStoryArtifact.version.publishedAt
    },
    {
      key: 'revoke',
      titleKey: 'lots.story.library.detail.revokedBy' as const,
      identityLabel: resolveStoredActorContextLabel(selectedStoryArtifact.artifact.revokedByContext, undefined, ''),
      actorId: selectedStoryArtifact.artifact.revokedByContext?.actorId ?? selectedStoryArtifact.artifact.revokedByActorId,
      tenantMemberId: selectedStoryArtifact.artifact.revokedByContext?.tenantMemberId,
      userId: selectedStoryArtifact.artifact.revokedByContext?.userId,
      tenantMembershipId: selectedStoryArtifact.artifact.revokedByContext?.tenantMembershipId,
      tokenId: selectedStoryArtifact.artifact.revokedByContext?.tokenId ?? selectedStoryArtifact.artifact.revokedByTokenId,
      tenantKey: selectedStoryArtifact.artifact.revokedByContext?.tenantKey ?? selectedStoryArtifact.artifact.tenantKey,
      recordedAt: selectedStoryArtifact.artifact.revokedAt
    }
  ].filter((card) => card.actorId || card.tenantMemberId || card.userId || card.tenantMembershipId || card.tokenId || card.recordedAt)
  const isGroupIntentSaved = storyGroupShareReviewPreflight.selectedGroupMatchesSavedIntent
  const isGroupPromotionRecorded = storyGroupShareReviewPreflight.promotionRecorded
  const hasEditorialNotes = Boolean(selectedStoryArtifact.version.summary || selectedStoryArtifact.version.notes)
  const compactStatusPills = [
    {
      key: 'version-status',
      label: t('lots.story.library.detail.currentVersionStatus'),
      value: getLotStoryArtifactVersionLabel(t, selectedStoryArtifact.version.versionStatus)
    },
    {
      key: 'visibility',
      label: t('lots.story.library.detail.visibility'),
      value: getLotStoryArtifactVisibilityLabel(t, selectedStoryArtifact.version.visibility)
    },
    {
      key: 'readiness',
      label: t('lots.story.library.detail.readiness'),
      value: getLotStoryReadinessLabel(t, selectedStoryArtifact.version.readinessStatus)
    }
  ]
  const compactMetaRows = [
    t('lots.story.library.detail.version', { version: selectedStoryArtifact.version.versionNumber }),
    `${t('lots.story.summary.generatedAt')}: ${formatHistoryTimestamp(selectedStoryArtifact.version.projectionGeneratedAt)}`,
    `${t('lots.story.library.detail.publishedAt')}: ${selectedStoryArtifact.version.publishedAt
      ? formatHistoryTimestamp(selectedStoryArtifact.version.publishedAt)
      : '—'}`
  ]

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ display: 'grid', gap: 8 }}>
          <strong style={{ display: 'block', fontSize: 16, color: '#111827' }}>{selectedStoryArtifact.version.title}</strong>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {compactStatusPills.map((pill) => (
              <span
                key={pill.key}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '4px 8px',
                  borderRadius: 999,
                  backgroundColor: '#fef3c7',
                  color: '#92400e',
                  fontSize: 11,
                  fontWeight: 600
                }}
              >
                <span style={{ opacity: 0.8 }}>{pill.label}:</span>
                <span>{pill.value}</span>
              </span>
            ))}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', fontSize: 12, color: '#475569' }}>
            {compactMetaRows.map((entry) => (
              <span key={entry}>{entry}</span>
            ))}
          </div>
        </div>
        <span style={{ ...getLotStoryArtifactStatusBadgeStyle(selectedStoryArtifact.artifact.status), padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3 }}>
          {getLotStoryArtifactStatusLabel(t, selectedStoryArtifact.artifact.status)}
        </span>
      </div>

      <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'minmax(0, 1fr)' }}>
        <div style={lotStoryCardStyle}>
          <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.summary.lineageRoot')}</strong>
          <div style={{ display: 'grid', gap: 4 }}>
            <div style={{ fontSize: 14, color: '#111827', wordBreak: 'break-word' }}>{selectedStoryFamilyLabel}</div>
            {selectedStoryFamilyTechnicalId ? (
              <div style={{ fontSize: 12, color: '#92400e', wordBreak: 'break-word' }}>{selectedStoryFamilyTechnicalId}</div>
            ) : null}
          </div>
        </div>
      </div>

      {hasEditorialNotes ? (
        <DisclosureSection
          title={t('lots.story.library.detail.editorialNotesTitle')}
          subtitle={t('lots.story.library.detail.editorialNotesSubtitle')}
        >
          {selectedStoryArtifact.version.summary ? (
            <div style={{ display: 'grid', gap: 4 }}>
              <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.draft.field.summary')}</strong>
              <div style={{ fontSize: 13, color: '#111827' }}>{selectedStoryArtifact.version.summary}</div>
            </div>
          ) : null}

          {selectedStoryArtifact.version.notes ? (
            <div style={{ display: 'grid', gap: 4 }}>
              <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.draft.field.notes')}</strong>
              <div style={{ fontSize: 13, color: '#111827', whiteSpace: 'pre-wrap' }}>{selectedStoryArtifact.version.notes}</div>
            </div>
          ) : null}
        </DisclosureSection>
      ) : null}

      <DisclosureSection
        title={t('lots.story.library.detail.artifactContextTitle')}
        subtitle={t('lots.story.library.detail.artifactContextSubtitle')}
        defaultOpen={selectedStoryArtifact.version.versionStatus === 'draft'}
      >
        <LotStoryPrivateWritePreflightCard
          t={t}
          preflight={storyPrivateWritePreflight}
          testIdPrefix="lot-story-library-private-preflight"
        />
      </DisclosureSection>

      <DisclosureSection
        title={t('lots.story.library.detail.groupSetupTitle')}
        subtitle={t('lots.story.library.detail.groupSetupSubtitle')}
        defaultOpen
      >
        <LotStoryGroupReadPreflightCard
          t={t}
          preflight={storyGroupReadPreflight}
          testIdPrefix="lot-story-library-group-read-preflight"
          onSelectGroup={onSelectGroupReadPreflightGroup}
        />

        <LotStoryGroupShareShellCard
          t={t}
          shareShell={storyGroupShareShell}
          testIdPrefix="lot-story-library-group-share-shell"
          onSaveTargetGroup={onSaveGroupShareIntent}
        />
      </DisclosureSection>

      <DisclosureSection
        title={t('lots.story.library.detail.groupPromotionTitle')}
        subtitle={t('lots.story.library.detail.groupPromotionSubtitle')}
        defaultOpen
      >
        <LotStoryGroupShareReviewPreflightCard
          t={t}
          preflight={storyGroupShareReviewPreflight}
          testIdPrefix="lot-story-library-group-share-review-preflight"
          onRecordReview={onRecordGroupShareReview}
          onRecordApproval={onRecordGroupShareApproval}
          onRecordReleaseGate={onRecordGroupShareReleaseGate}
          onPromoteToGroup={onPromoteStoryToGroup}
        />
      </DisclosureSection>

      {selectedStoryArtifact.artifact.visibility === 'group' ? (
        <DisclosureSection
          title={t('lots.story.library.detail.groupDeliveryTitle')}
          subtitle={t('lots.story.library.detail.groupDeliverySubtitle')}
          defaultOpen
        >
          <LotStoryGroupDeliveryCard
            t={t}
            delivery={storyGroupDelivery}
            testIdPrefix="lot-story-library-group-delivery"
            renderedProjection={storyGroupDelivery.projection
              ? renderStoryProjectionReadOnlySections({
                  projection: storyGroupDelivery.projection,
                  keyPrefix: `lot-story-library-group-delivery-${selectedStoryArtifact.artifact.artifactId}`,
                  accentText: '#166534',
                  accentBorder: '#bbf7d0',
                  compact: true
                })
              : undefined}
            renderedReceipt={storyGroupDeliveryRenderedReceipt}
          />
        </DisclosureSection>
      ) : null}

      {attributionCards.length > 0 ? (
        <DisclosureSection
          title={t('lots.story.library.detail.attribution')}
          subtitle={t('lots.story.library.detail.attributionSubtitle')}
          defaultOpen
        >
          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
            {attributionCards.map((card) => (
              <div key={card.key} style={lotStoryCardStyle}>
                <strong style={{ fontSize: 12, color: '#92400e' }}>{t(card.titleKey)}</strong>
                <div style={{ display: 'grid', gap: 4, marginTop: 4, fontSize: 13, color: '#111827' }}>
                  {card.identityLabel && card.identityLabel !== (card.actorId?.trim() || '') ? (
                    <div>
                      {t('lots.story.library.detail.presentationIdentity', {
                        value: card.identityLabel
                      })}
                    </div>
                  ) : null}
                  {card.tenantKey ? (
                    <div>
                      {t('lots.story.library.detail.tenantKey', {
                        value: card.tenantKey
                      })}
                    </div>
                  ) : null}
                  {card.tenantMemberId ? (
                    <div>
                      {t('lots.story.library.detail.tenantMemberId', {
                        value: card.tenantMemberId
                      })}
                    </div>
                  ) : null}
                  {card.userId ? (
                    <div>
                      {t('lots.story.library.detail.userId', {
                        value: card.userId
                      })}
                    </div>
                  ) : null}
                  {card.tenantMembershipId ? (
                    <div>
                      {t('lots.story.library.detail.tenantMembershipId', {
                        value: card.tenantMembershipId
                      })}
                    </div>
                  ) : null}
                  <div>
                    {t('lots.story.library.detail.actorId', {
                      value: card.actorId?.trim() || '—'
                    })}
                  </div>
                  {card.tokenId ? (
                    <div style={{ fontSize: 12, color: '#475569', wordBreak: 'break-word' }}>
                      {t('lots.story.library.detail.tokenId', { value: card.tokenId })}
                    </div>
                  ) : null}
                  {card.recordedAt ? (
                    <div style={{ fontSize: 12, color: '#475569' }}>
                      {t('lots.story.library.detail.recordedAt', {
                        value: formatHistoryTimestamp(card.recordedAt)
                      })}
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </DisclosureSection>
      ) : null}

      <DisclosureSection
        title={t('lots.story.library.history.title')}
        subtitle={t('lots.story.library.detail.historySubtitle')}
      >
        <LotStoryLibraryVersionHistory
          t={t}
          artifact={selectedStoryArtifact}
          selectedStoryPreviewVersionId={selectedStoryPreviewVersionId}
          onOpenArtifactVersion={onOpenArtifactVersion}
          presentation={presentation}
        />
      </DisclosureSection>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
        {canDraftLotStories && selectedStoryArtifact.version.versionStatus === 'draft' ? (
          <GradientButton
            type="button"
            onClick={onPublishArtifact}
            disabled={storyPublishing || storyRevoking || storyPrivateWritePreflight.status !== 'allowed'}
            data-testid="lot-story-library-publish-button"
          >
            {storyPublishing ? t('lots.story.publish.publishing') : t('lots.story.publish.button')}
          </GradientButton>
        ) : null}
        {canRevokeLotStories && selectedStoryArtifact.artifact.status === 'published' && selectedStoryArtifact.version.versionStatus === 'published' ? (
          <GradientButton
            type="button"
            onClick={onRevokeArtifact}
            disabled={storyRevoking || storyPrivateWritePreflight.status !== 'allowed'}
            data-testid="lot-story-library-revoke-button"
          >
            {storyRevoking ? t('lots.story.revoke.revoking') : t('lots.story.revoke.button')}
          </GradientButton>
        ) : null}
        <GradientButton type="button" onClick={() => onOpenArtifact(selectedStoryArtifact)}>
          {t('lots.story.library.openPreview')}
        </GradientButton>
      </div>
    </>
  )
}
